import { useState } from "react";
import { Fingerprint, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { authClient } from "@/lib/auth-client";

interface PasskeyStepProps {
  onSuccess: () => void;
  onBack: () => void;
  stepNumber: 1 | 2;
}

export const PasskeyStep = ({
  onSuccess,
  onBack,
  stepNumber,
}: PasskeyStepProps) => {
  const [loading, setLoading] = useState(false);

  const handleAuthenticate = async () => {
    setLoading(true);
    try {
      const { data, error } = await authClient.signIn.passkey();

      if (error) {
        throw error;
      }

      toast.success("Authenticated successfully");
      onSuccess();
    } catch (error: any) {
      console.error("Passkey authentication error:", error);
      toast.error(error.message || "Failed to authenticate with passkey");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Fingerprint className="h-5 w-5" />
          {stepNumber === 1 ? "Sign In With Passkey" : "Verify With Passkey"}
        </CardTitle>
        <CardDescription>
          {stepNumber === 1
            ? "Use your device's biometric authentication or security key"
            : "Complete authentication with your passkey"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-center space-y-4 py-6">
          <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
            <Fingerprint className="h-10 w-10 text-primary" />
          </div>
          <p className="text-sm text-muted-foreground">
            Click the button below to authenticate with your passkey.
            {stepNumber === 2 && " This will complete your sign-in."}
          </p>
        </div>

        <div className="space-y-3">
          <Button
            type="button"
            className="w-full"
            onClick={handleAuthenticate}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Authenticating...
              </>
            ) : (
              <>
                <Fingerprint className="mr-2 h-4 w-4" />
                Authenticate with Passkey
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={onBack}
            disabled={loading}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>

        <div className="text-xs text-center text-muted-foreground space-y-1">
          <p>Your device will prompt you to:</p>
          <ul className="list-disc list-inside text-left space-y-0.5">
            <li>Use fingerprint or face recognition</li>
            <li>Insert and tap your security key</li>
            <li>Enter your device PIN</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
